import { FC, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { GET_ALL_STORAGES } from '../../../query/storage-activity'
import { useQuery } from '@apollo/client'
import { IStorage } from './types'
import { getStoragesVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'

import './index.scss'
import { EXPORT_STORAGE_QUERY } from '../../../query/export-storage'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'

const StorageActivity: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IStorage[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [storagesVars, setStoragesVars] = useState<any>(getStoragesVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, error, refetch } = useQuery(GET_ALL_STORAGES, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getStoragesVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.storageActivities.pageInfo.endCursor)
    setStartCursor(data.storageActivities.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '/food/storage-activity/storage',
      'storageActivities',
      'storageActivity'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setStoragesVars({ ...filterEnums(tableSearch, getStoragesVars, data) })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getStoragesVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'StorageActivities',
      query: EXPORT_STORAGE_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[{ path: '/food/storage-activity', title: 'Storage Activity Table' }]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Storage Activity</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button onClick={() => navigate('/food/storage-activity/storage?itemId=new')}>
            Add Storage
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={storagesVars || getStoragesVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default StorageActivity
